import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '@/state/store/auth';
import { useGetOrganizationByOrgId } from '@/graphql/queries/organizations';
import { LoadingOverlay } from '@/components/core';
import { useGetTemplateBasicById } from '@/modules/templates/hooks/use-templates';
import { useGetTemplateDocxsByTemplateId } from '@/modules/templates/hooks/use-template-docx';
import { useGetTemplateVariablesByTemplateId } from '@/modules/templates/hooks/use-template-variable';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import DocxTemplateBuilder from '../components/docx-template-builder';

const OrgTemplateDocxBuilderPage = () => {
  const { orgId: slug, id } = useParams<{ orgId: string; id: string }>();
  const navigate = useNavigate();
  const { activeOrgRole } = useAuthStore();
  const currentOrgId = useCurrentOrgId();
  const { data: org, isLoading } = useGetOrganizationByOrgId(slug);
  // Basic, not WithScreensAndFields: a fixed-DOCX template has neither, and the fuller query
  // costs extra serial round trips in front of the blocking loader below.
  const { data: template, isLoading: isTemplateLoading } = useGetTemplateBasicById(id);

  // Warm the two queries the builder needs next. Both key off the route id alone, but they live
  // inside DocxSelectionProvider — which does not mount until the loader below clears, so they
  // used to queue up behind org+template instead of running alongside them. Same query keys, so
  // the provider reads them straight from cache. This is the difference between four serial
  // round trips before the .docx download can start and two.
  useGetTemplateDocxsByTemplateId(id);
  useGetTemplateVariablesByTemplateId(id);

  if (activeOrgRole !== 'org_admin' && activeOrgRole !== 'org_editor') {
    navigate(`/org/${slug}`, { replace: true });
    return null;
  }

  if (isLoading || isTemplateLoading) {
    return <LoadingOverlay />;
  }

  if (!org) return null;

  if (!template) {
    navigate(`/org/${slug}/templates`);
    return null;
  }

  if (template.kind !== 'fixed_docx') {
    navigate(`/org/${slug}/templates/${id}/edit`);
    return null;
  }

  return (
    <DocxTemplateBuilder template={template} orgSlug={slug ?? ''} orgId={currentOrgId ?? ''} />
  );
};

export default OrgTemplateDocxBuilderPage;
